import { useState, useEffect, useCallback, useRef } from 'react';
import { PushService } from '../services';

interface NotificationStatus {
  permission: NotificationPermission;
  isSupported: boolean;
  isPushSupported: boolean;
  hasActiveSubscription: boolean;
  needsAttention: boolean;
}

interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export const useNotificationPermission = () => {
  const [status, setStatus] = useState<NotificationStatus>({
    permission: 'default',
    isSupported: false,
    isPushSupported: false,
    hasActiveSubscription: false,
    needsAttention: false
  });

  // Controle para evitar execuções duplicadas
  const [isRegistering, setIsRegistering] = useState(false);
  const [lastRegistrationAttempt, setLastRegistrationAttempt] = useState(0);
  const [lastSyncAttempt, setLastSyncAttempt] = useState(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const lastStatusUpdateRef = useRef(0);

  const checkNotificationSupport = useCallback(() => {
    const isSupported = 'Notification' in window;
    const isPushSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    
    return { isSupported, isPushSupported };
  }, []);

  const checkPermissionStatus = useCallback(() => {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }, []);

  const syncLocalSubscriptionToBackend = useCallback(async (subscription: PushSubscription) => {
    try {
      const endpointHash = btoa(subscription.endpoint).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
      const deviceId = `device_${endpointHash}`;
      
      const getDeviceName = () => {
        const userAgent = navigator.userAgent;
        if (/android/i.test(userAgent)) {
          return 'Dispositivo Android';
        } else if (/iphone|ipad|ipod/i.test(userAgent)) {
          return 'Dispositivo iOS';
        } else if (/windows/i.test(userAgent)) {
          return 'Dispositivo Windows';
        } else if (/macintosh|mac os x/i.test(userAgent)) {
          return 'Dispositivo macOS';
        } else if (/linux/i.test(userAgent)) {
          return 'Dispositivo Linux';
        }
        return 'Dispositivo Desconhecido';
      };

      const pushSubscription = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, 
            new Uint8Array((subscription as any).getKey('p256dh') || [])
          )),
          auth: btoa(String.fromCharCode.apply(null, 
            new Uint8Array((subscription as any).getKey('auth') || [])
          ))
        },
        device_id: deviceId,
        device_name: getDeviceName(),
        user_agent: navigator.userAgent
      } as PushSubscription;

      const result = await PushService.registerPushSubscriptionWithDevice(pushSubscription);
      return true;

    } catch (error) {
      console.error('Erro ao sincronizar assinatura com o backend:', error);
      return false;
    }
  }, []);

  const checkPushSubscription = useCallback(async () => {
    if (!('serviceWorker' in navigator && 'PushManager' in window)) {
      return false;
    }

    // Evitar verificações simultâneas
    if (isCheckingStatus) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        return false;
      }

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        return false;
      }

      // Verificar se a assinatura está registrada no backend
      try {
        setIsCheckingStatus(true);
        const data = await PushService.getPushStatus();
        const hasBackendSubscription = data.hasActiveSubscription === true;
        
        // Se tem assinatura local mas não no backend, sincronizar automaticamente
        // Mas apenas se não tentou sincronizar recentemente (últimos 5 minutos)
        if (!hasBackendSubscription) {
          const now = Date.now();
          const timeSinceLastSync = now - lastSyncAttempt;
          
          if (timeSinceLastSync > 300000) { // 5 minutos
            await syncLocalSubscriptionToBackend(subscription as any);
            setLastSyncAttempt(now);
            return true; // Retorna true pois agora está sincronizado
          } else {
          }
        }
        
        return hasBackendSubscription;
      } catch (error) {
        console.warn('Erro ao verificar status no backend, usando verificação local:', error);
      } finally {
        setIsCheckingStatus(false);
      }

      // Fallback: se não conseguir verificar no backend, usa verificação local
      return subscription !== null;
    } catch (error) {
      console.error('Erro ao verificar assinatura push:', error);
      setIsCheckingStatus(false);
      return false;
    }
  }, [syncLocalSubscriptionToBackend, isCheckingStatus, lastSyncAttempt]);

  const updateStatus = useCallback(async () => {
    // Throttle: evitar atualizações muito frequentes (máximo 1 a cada 5 segundos)
    const now = Date.now();
    if (now - lastStatusUpdateRef.current < 5000) {
      return;
    }
    lastStatusUpdateRef.current = now;

    const { isSupported, isPushSupported } = checkNotificationSupport();
    const permission = checkPermissionStatus();
    const hasActiveSubscription = await checkPushSubscription();
    
    // Determina se precisa de atenção:
    // - Navegador suporta notificações E push é suportado
    // - Permissão não foi concedida OU não tem assinatura push ativa
    // - Não mostrar se permissão foi explicitamente negada (usuário já decidiu)
    const needsAttention = isSupported && isPushSupported && 
      permission !== 'denied' &&
      (permission !== 'granted' || (permission === 'granted' && !hasActiveSubscription));

    setStatus({
      permission,
      isSupported,
      isPushSupported,
      hasActiveSubscription,
      needsAttention
    });
  }, [checkNotificationSupport, checkPermissionStatus, checkPushSubscription]);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      throw new Error('Este navegador não suporta notificações');
    }

    // Verificar se já foi negada - não tentar novamente automaticamente
    if (Notification.permission === 'denied') {
      throw new Error('Permissão de notificação foi negada. Ative nas configurações do navegador.');
    }

    // Se já foi concedida, retornar imediatamente
    if (Notification.permission === 'granted') {
      return 'granted';
    }

    // Verificar se push notifications são suportadas antes de solicitar
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Push notifications não são suportadas neste navegador.');
    }

    try {
      // Solicitar permissão apenas quando o usuário interagir explicitamente
      // (não chamar automaticamente - deve ser chamado por ação do usuário)
      const permission = await Notification.requestPermission();
      await updateStatus(); // Atualiza o status após mudança de permissão
      return permission;
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
      throw error;
    }
  }, [updateStatus]);

  // Função para registrar push subscription automaticamente quando permissão é concedida
  const registerPushSubscriptionAutomatically = useCallback(async () => {
    // Evitar execuções duplicadas
    if (isRegistering) {
      return false;
    }

    // Evitar execuções muito frequentes (máximo 1 por minuto)
    const now = Date.now();
    if (now - lastRegistrationAttempt < 60000) {
      return false;
    }

    if (!('serviceWorker' in navigator && 'PushManager' in window)) {
      return false;
    }

    setIsRegistering(true);
    setLastRegistrationAttempt(now);

    try {
      // Verificar se já tem assinatura local
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        return false;
      }

      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Já tem assinatura local, verificar se já foi registrada recentemente
        const lastRegistered = localStorage.getItem('lastPushRegistration');
        const now = Date.now();
        if (lastRegistered && (now - parseInt(lastRegistered)) < 300000) { // 5 minutos
          return false;
        }
        
        await syncLocalSubscriptionToBackend(subscription as any);
        localStorage.setItem('lastPushRegistration', now.toString());
        return true;
      }

      // Não tem assinatura local, tentar criar uma
      if (Notification.permission === 'granted') {
        
        // Buscar chave VAPID usando PushService
        const vapidData = await PushService.getVapidPublicKey();
        if (!vapidData.success || !vapidData.public_key) {
          console.warn('Chave VAPID não encontrada');
          return false;
        }

        // Converter chave VAPID
        const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
          const padding = '='.repeat((4 - base64String.length % 4) % 4);
          const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');
          const rawData = window.atob(base64);
          const outputArray = new Uint8Array(rawData.length);
          for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
          }
          return outputArray;
        };

        // Criar assinatura push
        const newSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidData.public_key) as any
        });

        // Registrar no backend
        await syncLocalSubscriptionToBackend(newSubscription as any);
        localStorage.setItem('lastPushRegistration', now.toString());
        return true;
      }

      return false;
    } catch (error) {
      console.error('Erro ao registrar push subscription automaticamente:', error);
      return false;
    } finally {
      setIsRegistering(false);
    }
  }, [syncLocalSubscriptionToBackend, isRegistering, lastRegistrationAttempt]);

  // Effect para monitorar mudanças
  useEffect(() => {
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    // Função para verificar e registrar automaticamente se necessário
    const checkAndRegisterAutomatically = async () => {
      // Só verifica se o usuário já tem permissão concedida
      if (Notification.permission === 'granted' && isMounted) {
        await registerPushSubscriptionAutomatically();
      }
    };

    // Verifica imediatamente ao carregar (com delay para evitar execução imediata)
    const initialTimeout = setTimeout(() => {
      if (isMounted) {
        checkAndRegisterAutomatically();
        updateStatus();
      }
    }, 1000); // Delay de 1 segundo

    // Listener para mudanças de visibilidade da página
    const handleVisibilityChange = () => {
      if (!document.hidden && isMounted) {
        // Throttle: só atualiza se passou pelo menos 10 segundos desde a última atualização
        const now = Date.now();
        if (now - lastStatusUpdateRef.current > 10000) {
          updateStatus();
          // Verifica novamente quando a página fica visível
          checkAndRegisterAutomatically();
        }
      }
    };

    // Listener para mudanças no service worker
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'PUSH_SUBSCRIPTION_CHANGED' && isMounted) {
        updateStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Verifica periodicamente (a cada 2 minutos quando a página está ativa)
    // Reduzido de 30s para 2min para evitar loops
    intervalId = setInterval(async () => {
      if (!document.hidden && isMounted) {
        await updateStatus();
        // Verifica se precisa registrar automaticamente
        checkAndRegisterAutomatically();
      }
    }, 120000); // 2 minutos

    return () => {
      isMounted = false;
      clearTimeout(initialTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
      
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [updateStatus, registerPushSubscriptionAutomatically]);

  return {
    ...status,
    requestPermission,
    refreshStatus: updateStatus
  };
};